// src/lib/data.ts
import type { Book, BlogPost, Review } from '@/types';

/**
 * Global checkout settings
 */
export const PAYPAL_LINK = 'https://paypal.me/timelessedu';
export const CURRENCY = 'EUR';

/**
 * Top-seller counters (read by slug in UI)
 * 1) Untangling the Spark - 2000
 * 2) Please don\'t say you love me - Fear Untangled - 1500
 * 3) Dark Psychology Detection - 1800
 */
export const bookSales: Record<string, number> = {
 'untangling-the-spark': 2000,
 'please-dont-say-you-love-me-fear-untangled': 1500,
 'dark-psychology-detection': 1800,
};

// -------------------
// BOOKS (all €4.99)
// -------------------
export const books: Book[] = [
 // MAIN THREE
 {
 id: 1,
 slug: 'untangling-the-spark',
 title:
 'Untangling the Spark - The Echo in Your Heart and a Soft Place to Land After Narcissistic Abuse',
 subtitle: 'See the pattern. Break the bond. Come home to yourself.',
 description:
 'A gentle, practical path out of narcissistic dynamics. Understand love-bombing, trauma bonding, boundary repair, and how to rebuild a self that feels steady and safe.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Modern Love',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'The Spark That Burns',
 'The Echo in Your Heart',
 'Attachment: Anxious-Avoidant Loops',
 'Love-Bomb -> Devalue -> Discard',
 'Trauma Bonding & Withdrawal',
 'Boundaries that Hold',
 'A Healthy Blueprint for Real Love',
 ],
 },
 {
 id: 2,
 slug: 'please-dont-say-you-love-me-fear-untangled',
 title: "Please Don't Say You Love Me - Fear Untangled",
 subtitle: 'From mixed signals to conscious connection.',
 description:
 'Puts words to the modern dating fog, untangles fear-based patterns, and gives you scripts and practices to speak clearly, choose wisely, and protect your peace.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Modern Love',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'When "I love you" Means "Don\'t Leave Me"',
 'Attachment & Avoidance in Real Life',
 'Respect, Safety, and Standards',
 'Clear Communication, Clean Boundaries',
 'Green Flags: What Healthy Feels Like',
 ],
 },
 {
 id: 3,
 slug: 'dark-psychology-detection',
 title: 'Dark Psychology Detection',
 subtitle: 'Spot the tactics. Keep your power.',
 description:
 'Your field guide to gaslighting, time-pressure plays, triangulation, information control, and the difference between ethical influence and manipulation.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'The Shadow Side of Human Psychology',
 'The Mind Games Manipulators Play',
 'Influence vs Manipulation',
 'The Psychology of Defense',
 'Mastering Ethical Influence',
 'Moving Forward with Confidence',
 ],
 },

 // ADDITIONAL CATALOG
 {
 id: 4,
 slug: 'narcissistic-behaviour-and-relationships',
 title: 'Narcissistic Behaviour and Relationships',
 subtitle: 'See the pattern. Reclaim your power.',
 description:
 'A clear, compassionate guide to spotting narcissistic dynamics, setting boundaries, and rebuilding your sense of self-without the guilt.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Modern Love',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'The Spark That Burns',
 'Why We\'re Drawn to the Unavailable',
 'Anxious Hearts, Avoidant Games',
 'Love-Bomb, Devalue, Discard',
 'Trauma Bonding & Detachment',
 'Healing, Boundaries, Self-Respect',
 'A Healthy Blueprint for Connection',
 ],
 },
 {
 id: 6,
 slug: 'rewriting-your-story',
 title: 'Rewriting Your Story',
 subtitle: 'Edit your narrative. Change your life.',
 description:
 'Spot limiting scripts and write a truer one. Evidence-based reframes that upgrade identity, choices, and the future you\'re creating.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'The Stories We Inherit',
 'Choice Points & Identity',
 'Narrative Reframes',
 'Living the New Draft',
 ],
 },
 {
 id: 7,
 slug: 'develop-your-emotional-intelligence',
 title: 'Develop Your Emotional Intelligence',
 subtitle: 'Feel more. React less. Connect better.',
 description:
 'Practical EI drills for real life: conversation templates, repair steps, and confidence that holds in uncomfortable moments.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'Signals vs Stories',
 'Truth with Care',
 'Repair & Reconnect',
 'Leading with EQ',
 ],
 },
 {
 id: 8,
 slug: 'emotional-self-awareness',
 title: 'Emotional Self Awareness',
 subtitle: 'Name it. Feel it. Choose your response.',
 description:
 'Build the inner clarity that changes outer results. Track triggers, translate sensations, and stay honest without self-abandonment.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'From Sensation to Signal',
 'Journaling for Clarity',
 'Needs, Limits & Requests',
 'Self-Compassion in Practice',
 ],
 },
 {
 id: 9,
 slug: 'the-calm-beyond-anxiety',
 title: 'The Calm Beyond Anxiety',
 subtitle: 'Tools to meet anxious moments-and move through.',
 description:
 'From spirals to steadiness: nervous-system basics, breath patterns, and cognitive tools that help you interrupt the loop and regain choice.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Mindful Living',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'How Anxiety Hooks Attention',
 'Physiology: Breath & Pace',
 'Thought Work (without Toxic Positivity)',
 'Daily Calm Routines',
 ],
 },
 {
 id: 10,
 slug: 'the-culture-of-care',
 title: 'The Culture of Care',
 subtitle: 'Lead with humanity-at home and at work.',
 description:
 'Principles and practices for building relationships and teams that blend candor with care.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'Psychological Safety by Design',
 'Boundaries & Expectations',
 'Feedback that Lands',
 'Rituals of Respect',
 ],
 },
 {
 id: 11,
 slug: 'worth-while-life',
 title: 'Worth While Life',
 subtitle: 'Build a life that feels like you.',
 description:
 'Define your values, design your weeks, and commit to a path that makes sense in your bones.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: ['Values, Not Vibes', 'Energy Mapping', 'Honest Tradeoffs', 'A Worthwhile Year'],
 },
 {
 id: 12,
 slug: 'the-art-of-consistency',
 title: 'The Art Of Consistency',
 subtitle: 'Small steps, repeated-until it\'s you.',
 description:
 'Design habits that last: friction-aware planning, identity shifts, and recovery so consistency survives real life.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: ['Why Motivation Fades', 'Make It Obvious & Easy', 'Identity-Based Habits', 'Recover & Continue'],
 },
 {
 id: 13,
 slug: 'level-up-your-emotional-intelligence',
 title: 'Level Up Your Emotional Intelligence',
 subtitle: 'Understand feelings. Navigate people. Lead yourself.',
 description:
 'A practical framework for reading emotions, responding with clarity, and creating relationships that respect both truth and care.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'Self-Awareness in Real Time',
 'Accurate Empathy (without Over-Giving)',
 'Honest Communication',
 'Repair, Boundaries & Trust',
 ],
 },
 {
 id: 14,
 slug: 'confidence-that-shows-before-you-speak',
 title: 'Confidence That Shows Before You Speak',
 subtitle: 'Calm presence. Clear voice. Real self-belief.',
 description:
 'Quiet self-doubt, regulate your nervous system, and project grounded confidence-in interviews, dates, and rooms that matter.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'Embodied Confidence',
 'Breath - Posture - Eye Contact',
 'Reframing Narratives',
 'Practice Routines',
 ],
 },
 {
 id: 15,
 slug: 'mindfulness-for-everyday-life',
 title: 'Mindfulness for Everyday Life',
 subtitle: 'Simple practices for a steadier day.',
 description:
 'Accessible micro-practices to anchor mornings, soften stress, and return to the present when life speeds up.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Mindful Living',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: ['Breath You Can Feel', 'Body Scan in Five Minutes', 'Mindful Walking & Sipping', 'A Calm System'],
 },
 {
 id: 16,
 slug: 'the-power-of-mindful-mornings',
 title: 'The Power of Mindful Mornings',
 subtitle: 'Build your calm with practical routines.',
 description:
 'A science-backed toolkit for creating a morning routine that actually lasts-habit loop, energy management, journaling & meditation.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Mindful Living',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'What Mindfulness Actually Does',
 'Your Brain\'s Habit Loops',
 'Journaling & Meditation',
 'Move Your Body, Prime Your Mind',
 ],
 },
 {
 id: 17,
 slug: 'the-power-of-attention',
 title: 'The Power of Attention',
 subtitle: 'What you focus on, you become.',
 description:
 'Train attention like a muscle. Reduce fragmentation, deepen presence, and make your best work-and relationships-possible.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Mindful Living',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'Attention as a Resource',
 'Designing Distraction-Free Space',
 'Single-Tasking in a Multi-Task World',
 'Deep Work, Deep Love',
 ],
 },
 {
 id: 18,
 slug: 'work-life-balance-for-ambitious-minds',
 title: 'Work Life Balance for Ambitious Minds',
 subtitle: 'Protect your drive-without burning out.',
 description:
 'A system for high performers who want both impact and inner steadiness: boundaries, energy, and recovery that fuels ambition.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'Ambition without Exhaustion',
 'Boundaries with Teams & Self',
 'Energy, Sleep & Cycles',
 'Saying No to Say Yes',
 ],
 },
 {
 id: 19,
 slug: 'start-manifesting',
 title: 'Start Manifesting',
 subtitle: 'From wishful to workable.',
 description:
 'Grounded manifestation: clarity, nervous-system alignment, and consistent acts that bring intentions into shape.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Mindful Living',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: ['From Vague to Specific', 'Align State & Steps', 'Micro-Commitments that Compound', 'Review & Receive'],
 },
 {
 id: 20,
 slug: 'transform-your-life',
 title: 'Transform Your Life',
 subtitle: 'End procrastination and build unstoppable self-confidence.',
 description:
  `Move from "I'll do it tomorrow" to "I did it today" with actionable strategies that rewire your brain for momentum.`,
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Personal Growth',
 formats: ['Ebook', 'Paperback'],
 tableOfContents: [
 'Root Causes of Procrastination',
 'The Five-Minute Rule',
 'Confidence Closet',
 'From Self-Doubt to Self-Belief',
 ],
 },
 {
 id: 21,
 slug: 'your-daily-calm-practice',
 title: 'Your Daily Calm Practice - Mini-Course',
 subtitle: 'Ten minutes a day to reset your system.',
 description:
 'A compact routine for breath, body-scan, and attention training-designed to fit even the busiest days.',
 coverImageId: 'book-default',
 price: 4.99,
 category: 'Mindful Living',
 formats: ['Ebook'],
 tableOfContents: ['Days 1-3: Breath', 'Days 4-6: Body Scan', 'Days 7-9: Attention Training', 'Day 10: Integrate'],
 },
];

// -------------------
// BLOG POSTS (all dates set in 2025)
// -------------------
export const blogPosts: BlogPost[] = [
 {
 id: 1,
 slug: 'untangling-the-spark-why-the-first-spark-can-burn',
 title: 'Untangling the Spark: Why the First "Spark" Can Burn',
 excerpt:
 'That electric beginning isn\'t always chemistry-it can be choreography. Here\'s how to tell the difference and protect your peace.',
 coverImageId: 'blog-post-1',
 date: '2025-01-07',
 relatedBookSlug: 'untangling-the-spark',
 },
 {
 id: 2,
 slug: 'fear-untangled-when-i-love-you-means-dont-leave-me',
 title: 'Fear Untangled: When "I Love You" Means "Don\'t Leave Me"',
 excerpt:
 'How anxious-avoidant loops create mixed signals-and how to step into clarity without losing yourself.',
 coverImageId: 'blog-post-2',
 date: '2025-01-14',
 relatedBookSlug: 'please-dont-say-you-love-me-fear-untangled',
 },
 {
 id: 3,
 slug: 'dark-psychology-detection-spot-the-tactics-keep-your-power',
 title: 'Dark Psychology Detection: Spot the Tactics, Keep Your Power',
 excerpt:
 'Gaslighting, moving goalposts, information control-know the patterns so you can keep your center.',
 coverImageId: 'blog-post-3',
 date: '2025-01-21',
 relatedBookSlug: 'dark-psychology-detection',
 },
 {
 id: 4,
 slug: 'seeing-the-pattern',
 title: 'Seeing the Pattern',
 excerpt:
 'Narcissistic cycles are predictable-here\'s how to notice the turn before it hurts.',
 coverImageId: 'blog-post-1',
 date: '2025-01-28',
 relatedBookSlug: 'narcissistic-behaviour-and-relationships',
 },
 {
 id: 5,
 slug: 'value-first-planning',
 title: 'Value-First Planning',
 excerpt:
 'If your week doesn\'t reflect your values, change the plan-not the values.',
 coverImageId: 'blog-post-2',
 date: '2025-02-04',
 },
 {
 id: 6,
 slug: 'rewrite-your-script',
 title: 'Rewrite Your Script',
 excerpt: 'A story is a strategy; choose a better one.',
 coverImageId: 'blog-post-3',
 date: '2025-02-11',
 relatedBookSlug: 'rewriting-your-story',
 },
 {
 id: 7,
 slug: 'speak-truth-with-care',
 title: 'Speak Truth with Care',
 excerpt: 'EQ is honesty plus skill. Here\'s the drill.',
 coverImageId: 'blog-post-1',
 date: '2025-02-18',
 relatedBookSlug: 'develop-your-emotional-intelligence',
 },
 {
 id: 8,
 slug: 'name-it-to-tame-it',
 title: 'Name It to Tame It',
 excerpt: 'Label the feeling; lower the load.',
 coverImageId: 'blog-post-2',
 date: '2025-02-25',
 relatedBookSlug: 'emotional-self-awareness',
 },
 {
 id: 9,
 slug: 'from-spiral-to-steady',
 title: 'From Spiral to Steady',
 excerpt:
 'A three-step reset for anxious moments that actually works.',
 coverImageId: 'blog-post-3',
 date: '2025-03-04',
 relatedBookSlug: 'the-calm-beyond-anxiety',
 },
 {
 id: 10,
 slug: 'care-with-candor',
 title: 'Care with Candor',
 excerpt: 'Psychological safety is designed, not declared.',
 coverImageId: 'blog-post-1',
 date: '2025-03-11',
 relatedBookSlug: 'the-culture-of-care',
 },
 {
 id: 11,
 slug: 'what-a-worthwhile-week-looks-like',
 title: 'What a Worth While Week Looks Like',
 excerpt: 'A week that feels like you-on paper.',
 coverImageId: 'blog-post-2',
 date: '2025-03-18',
 relatedBookSlug: 'worth-while-life',
 },
 {
 id: 12,
 slug: 'consistency-that-survives-real-life',
 title: 'Consistency that Survives Real Life',
 excerpt: 'Friction-aware planning beats motivation every time.',
 coverImageId: 'blog-post-3',
 date: '2025-03-25',
 relatedBookSlug: 'the-art-of-consistency',
 },
 {
 id: 13,
 slug: 'read-the-room-read-yourself',
 title: 'Read the Room, Read Yourself',
 excerpt: 'Better outcomes when empathy meets honesty.',
 coverImageId: 'blog-post-1',
 date: '2025-04-01',
 relatedBookSlug: 'level-up-your-emotional-intelligence',
 },
 {
 id: 14,
 slug: 'presence-before-words',
 title: 'Presence Before Words',
 excerpt: 'Confidence lands before you speak-here\'s how.',
 coverImageId: 'blog-post-2',
 date: '2025-04-08',
 relatedBookSlug: 'confidence-that-shows-before-you-speak',
 },
 {
 id: 15,
 slug: 'micro-practices-for-a-busy-life',
 title: 'Micro-Practices for a Busy Life',
 excerpt: 'Tiny anchors for steadier days.',
 coverImageId: 'blog-post-3',
 date: '2025-04-15',
 relatedBookSlug: 'mindfulness-for-everyday-life',
 },
 {
 id: 16,
 slug: 'win-the-morning',
 title: 'Win the Morning',
 excerpt:
 'Habit loops, low-friction starts, and momentum you can feel.',
 coverImageId: 'blog-post-1',
 date: '2025-04-22',
 relatedBookSlug: 'the-power-of-mindful-mornings',
 },
 {
 id: 17,
 slug: 'attention-is-a-superpower',
 title: 'Attention is a Superpower',
 excerpt: 'Reduce fragmentation. Build depth.',
 coverImageId: 'blog-post-2',
 date: '2025-04-29',
 relatedBookSlug: 'the-power-of-attention',
 },
 {
 id: 18,
 slug: 'ambition-without-exhaustion',
 title: 'Ambition Without Exhaustion',
 excerpt: 'Keep the fire-lose the burnout.',
 coverImageId: 'blog-post-3',
 date: '2025-05-06',
 relatedBookSlug: 'work-life-balance-for-ambitious-minds',
 },
 {
 id: 19,
 slug: 'from-wishful-to-workable',
 title: 'From Wishful to Workable',
 excerpt: 'State, steps, and the compound effect.',
 coverImageId: 'blog-post-1',
 date: '2025-05-13',
 relatedBookSlug: 'start-manifesting',
 },
 {
 id: 20,
 slug: 'momentum-on-demand',
 title: 'Momentum on Demand',
 excerpt: 'The five-minute rule and the confidence closet.',
 coverImageId: 'blog-post-2',
 date: '2025-05-20',
 relatedBookSlug: 'transform-your-life',
 },
 {
 id: 21,
 slug: 'ten-minutes-to-reset',
 title: 'Ten Minutes to Reset',
 excerpt: 'A simple daily calm loop you\'ll actually use.',
 coverImageId: 'blog-post-3',
 date: '2025-05-27',
 relatedBookSlug: 'your-daily-calm-practice',
 },
];

// -------------------
// REVIEWS (4.5–5 stars, with emphasis on the first three)
// -------------------
export const reviews: Review[] = [
 // Untangling the Spark (id:1)
 { id: 1, bookId: 1, rating: 5, author: 'Lara K.', text: 'Explained trauma bonding so I could finally unhook. Gentle and clear.' },
 { id: 2, bookId: 1, rating: 5, author: 'Maya S.', text: 'Felt seen for the first time. The boundary scripts = life-savers.' },
 { id: 3, bookId: 1, rating: 4.5, author: 'Tina R.', text: 'The "spark that burns" chapter changed everything for me.' },

 // Please don\'t say you love me - Fear Untangled (id:2)
 { id: 10, bookId: 2, rating: 5, author: 'Emily R.', text: 'Put words to the dating fog. I finally know what real safety feels like.' },
 { id: 11, bookId: 2, rating: 5, author: 'Zoe P.', text: 'Actionable, kind, and honest. I stopped chasing mixed signals.' },
 { id: 12, bookId: 2, rating: 4.5, author: 'Ana V.', text: 'The green-flags list helped me choose differently.' },

 // Dark Psychology Detection (id:3)
 { id: 20, bookId: 3, rating: 5, author: 'David P.', text: 'Clear patterns. Clean defenses. I\'m not getting played anymore.' },
 { id: 21, bookId: 3, rating: 5, author: 'Sophie L.', text: 'Finally understand the difference between influence and manipulation.' },
 { id: 22, bookId: 3, rating: 4.5, author: 'Ken M.', text: 'Gaslighting section alone was worth it.' },

 // Narcissistic Behaviour and Relationships (id:4)
 { id: 30, bookId: 4, rating: 5, author: 'Lara K.', text: 'Explained trauma bonding so I could finally unhook. Gentle and clear.' },
 { id: 31, bookId: 4, rating: 4.5, author: 'Rhea D.', text: 'Helped me set a standard and keep it.' },

 // A sampling for the rest (2 each)
 { id: 40, bookId: 5, rating: 5, author: 'Jon A.', text: 'My calendar finally matches my values.' },
 { id: 41, bookId: 5, rating: 4.5, author: 'Priya S.', text: 'The "eliminate, delegate, automate" flow is gold.' },

 { id: 50, bookId: 6, rating: 5, author: 'Hanna G.', text: 'Reframed a story I\'d carried for years.' },
 { id: 51, bookId: 6, rating: 4.5, author: 'Alex T.', text: 'Short, sharp, effective.' },

 { id: 60, bookId: 7, rating: 5, author: 'Marco B.', text: 'Templates I actually use in tough conversations.' },
 { id: 61, bookId: 7, rating: 4.5, author: 'Kim L.', text: '"Truth with care" is my new motto.' },

 { id: 70, bookId: 8, rating: 5, author: 'Olivia C.', text: 'Naming it reduced the intensity by half.' },
 { id: 71, bookId: 8, rating: 4.5, author: 'Sam F.', text: 'Simple, human, powerful.' },

 { id: 80, bookId: 9, rating: 5, author: 'Viktor N.', text: 'A calm kit that actually works under pressure.' },
 { id: 81, bookId: 9, rating: 4.5, author: 'Lea J.', text: 'Breath patterns = instant reset.' },

 { id: 90, bookId: 10, rating: 5, author: 'Naomi W.', text: 'Transformed our 1:1s. More truth, more respect.' },
 { id: 91, bookId: 10, rating: 4.5, author: 'Ibrahim K.', text: 'Psych safety section is a keeper.' },

 { id: 100, bookId: 11, rating: 5, author: 'Chris D.', text: 'I finally like my week again.' },
 { id: 101, bookId: 11, rating: 4.5, author: 'Marta G.', text: 'Energy mapping was eye-opening.' },

 { id: 110, bookId: 12, rating: 5, author: 'Jay P.', text: 'Consistency finally survived real life.' },
 { id: 111, bookId: 12, rating: 4.5, author: 'Elena H.', text: 'Identity-based habits for the win.' },

 { id: 120, bookId: 13, rating: 5, author: 'Noah R.', text: 'My leadership got calmer and clearer.' },
 { id: 121, bookId: 13, rating: 4.5, author: 'Gina S.', text: 'Repair steps are gold.' },

 { id: 130, bookId: 14, rating: 5, author: 'Tara M.', text: 'Presence before words-game changer.' },
 { id: 131, bookId: 14, rating: 4.5, author: 'Owen L.', text: 'Grounded, not performative.' },

 { id: 140, bookId: 15, rating: 5, author: 'Rose Q.', text: 'Tiny practices, big calm.' },
 { id: 141, bookId: 15, rating: 4.5, author: 'Ben Z.', text: 'Body scan in five-perfect.' },

 { id: 150, bookId: 16, rating: 5, author: 'Jenna M.', text: 'Morning routine that actually stuck.' },
 { id: 151, bookId: 16, rating: 4.5, author: 'Sophie L.', text: 'No 5am club nonsense-just useful.' },

 { id: 160, bookId: 17, rating: 5, author: 'Uma V.', text: 'My focus feels like a superpower again.' },
 { id: 161, bookId: 17, rating: 4.5, author: 'Peter G.', text: 'Deep work, deeper relationships.' },

 { id: 170, bookId: 18, rating: 5, author: 'Andre C.', text: 'Ambitious and steady can coexist.' },
 { id: 171, bookId: 18, rating: 4.5, author: 'Kira J.', text: 'Boundaries I could finally keep.' },

 { id: 180, bookId: 19, rating: 5, author: 'Val S.', text: 'From vague to specific-now it moves.' },
 { id: 181, bookId: 19, rating: 4.5, author: 'Leo W.', text: 'State + steps = results.' },

 { id: 190, bookId: 20, rating: 5, author: 'David P.', text: 'Momentum on demand.' },
 { id: 191, bookId: 20, rating: 4.5, author: 'Nina B.', text: 'Confidence closet is brilliant.' },

 { id: 200, bookId: 21, rating: 5, author: 'Kim S.', text: 'Ten minutes and I feel different.' },
 { id: 201, bookId: 21, rating: 4.5, author: 'Milo R.', text: 'Finally a routine I can keep.' },
];
